import {
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import { ClientType, PackageType, PartnershipType } from '../../../types';
import { IRevenueShare } from '../../../utils/discount-utils';
import { ClientSelect } from '../../form-fields/client-select';
import { PartnershipSelect } from '../../form-fields/partnership-select';
import { ScopeSelector } from '../../form-fields/scope-selector';
import { ApplyToPriceCheckbox } from './components/apply-to-price-checkbox';
import { DiscountInput } from './components/discount-input';
import { EffectiveYearInput } from './components/effective-year-input';
import {
  DEFAULT_REVENUE_SHARE_FORM_VALUES,
  ParsedRevenueShareValues,
  RevenueShareFormValues,
} from './form-constants';
import { RevenueShareValidationSchema } from './validation-schema';

export interface RevenueShareFormProps {
  onSubmit: (values: ParsedRevenueShareValues) => void | Promise<void>;
  initialValues?: RevenueShareFormValues;
  id?: number;
  allRevenueShares?: IRevenueShare[];
  clients: ClientType[];
  clientsLoading: boolean;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  packages: PackageType[];
  wholeFormDisabled?: boolean;
}

export const RevenueShareForm = ({
  onSubmit,
  initialValues,
  id,
  allRevenueShares,
  clients,
  clientsLoading,
  partnerships,
  partnershipsLoading,
  packages,
  wholeFormDisabled,
}: RevenueShareFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<RevenueShareFormValues>({
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(RevenueShareValidationSchema),
    defaultValues: initialValues ?? DEFAULT_REVENUE_SHARE_FORM_VALUES,
    context: { allRevenueShares, editDefinitionId: id },
  });

  const {
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
  } = methods;

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const isEditMode = !!initialValues;

  const parseFormValues = (
    data: RevenueShareFormValues,
  ): ParsedRevenueShareValues => ({
    // ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
    clientId: data.clientId,
    partnershipId: data.partnershipId,
    effectiveYear: data.effectiveYear as number,
    discount: data.discount as number,
    applyToPrice: data.applyToPrice,
    scopes: data.scopes,
  });

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (
    data: RevenueShareFormValues,
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit(parseFormValues(data));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <HHStack spacing={6} direction="column">
          <HHTypography hhVariant="section-title">
            {isEditMode ? 'Edit Revenue Share' : 'Add Revenue Share'}
          </HHTypography>
          {/* ACCOUNTS-AND-PLAN-MIGRATION: errors.partnershipId/errors.clientId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration. */}
          <PartnershipSelect
            options={partnerships}
            loading={partnershipsLoading}
            disabled={isEditMode || wholeFormDisabled}
            error={errors.partnershipId}
            hint="Partnership is required. To create a client override also select a client."
          />
          <ClientSelect
            options={clients}
            loading={clientsLoading}
            disabled={isEditMode || wholeFormDisabled}
            label="Client (optional)"
            error={errors.clientId}
          />
          <ScopeSelector
            availablePackages={packages}
            disabled={wholeFormDisabled}
            entityLabel="revenue share"
          />
          <EffectiveYearInput disabled={wholeFormDisabled} />
          <DiscountInput error={errors.discount} disabled={wholeFormDisabled} />
          <ApplyToPriceCheckbox disabled={wholeFormDisabled} />
          <HHStack direction="row" spacing={2} justifyContent="flex-end">
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
            >
              Cancel
            </HHButton>
            <HHLoadingButton
              hhVariant="variant-bypass"
              variant="contained"
              type="submit"
              disabled={
                wholeFormDisabled || !isValid || !isDirty || isSubmitting
              }
              loading={isSubmitting}
            >
              {isEditMode ? 'Save' : 'Create'}
            </HHLoadingButton>
          </HHStack>
        </HHStack>
      </form>
    </FormProvider>
  );
};
